import React, { useEffect } from 'react';
import { AppShell, Group, Burger, Image, Button, Modal, Stack, Text, TextInput, Title } from '@mantine/core';
import { UserMenu } from '../Auth/UserMenu';
import { SessionSelector } from '../Auth/SessionSelector';
import type { Organization } from '@/models/auth.types';
import eliseAILogo from '@/assets/eliseai_logo.png';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { intakePortalApi } from '@/utils/api';
import { useCurrentSession } from '@/hooks/useCurrentSession';
import { useNotification } from '@/hooks/useNotification';
import { useAuth0 } from '@/hooks/useAuth0';
import { isInternalUser } from '@/utils/auth';
import { useQueryClient } from '@tanstack/react-query';

interface AppHeaderProps {
  opened: boolean;
  setOpened: (value: boolean | ((prev: boolean) => boolean)) => void;
  organization: Organization | null;
}

export const AppHeader = React.memo(({ opened, setOpened, organization }: AppHeaderProps) => {
  const { currentSessionId, selectSession, sessions, isLoading } = useCurrentSession();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth0();
  const queryClient = useQueryClient();
  const isInternal = isInternalUser(user?.email);

  const [inviteOpen, { open: openInvite, close: closeInvite }] = useDisclosure(false);
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);

  const inviteForm = useForm<{ email: string }>({
    initialValues: { email: '' },
    validate: {
      email: (value) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : 'Enter a valid email'),
    },
  });

  const createForm = useForm<{ title: string; orgId: string }>({
    initialValues: { title: '', orgId: '' },
    validate: {
      title: (value) => (value && value.trim().length > 0 ? null : 'Title is required'),
      orgId: (value) => {
        if (!isInternal) return null;

        const trimmedValue = value.trim();
        if (!trimmedValue) return 'Elise Org ID is required';
        if (!/^[1-9]\d*$/.test(trimmedValue)) return 'Enter a positive integer';

        const orgId = Number(trimmedValue);
        return Number.isSafeInteger(orgId) ? null : 'Enter a valid Elise Org ID';
      },
    },
  });

  // If user has no sessions, force open Create Launch Group modal
  const noSessions = !isLoading && sessions.length === 0;

  useEffect(() => {
    if (noSessions) {
      openCreate();
    }
  }, [noSessions, openCreate]);

  // Allow other components to trigger opening the Invite modal
  useEffect(() => {
    const handler = () => openInvite();
    window.addEventListener('open-invite-users', handler as EventListener);
    return () => window.removeEventListener('open-invite-users', handler as EventListener);
  }, [openInvite]);

  const handleInvite = async () => {
    const validation = inviteForm.validate();
    if (validation.hasErrors) return;
    if (!currentSessionId) {
      showError('No session selected');
      return;
    }
    try {
      await intakePortalApi.addUsersToSessionBySessionId(currentSessionId, [inviteForm.values.email.trim()]);
      showSuccess('Invitation sent');
      inviteForm.reset();
      closeInvite();
    } catch (e: any) {
      showError('Failed to invite user');
    }
  };

  const handleCreate = async () => {
    const validation = createForm.validate();
    if (validation.hasErrors) return;
    try {
      const orgId = isInternal ? Number(createForm.values.orgId.trim()) : undefined;
      const res = await intakePortalApi.createSessionFromUser(
        createForm.values.title.trim(),
        orgId,
      );
      showSuccess('Community group created');
      createForm.reset();
      closeCreate();
      await queryClient.invalidateQueries({ queryKey: ['user-sessions'] });
      selectSession(res.intake_portal_session_id);
    } catch (e: any) {
      showError('Failed to create launch group');
    }
  };

  return (
    <AppShell.Header style={{ overflow: 'visible' }}>
      <Group h="100%" px="md" justify="space-between" align="center">
        <Group align="center">
          <Burger
            opened={opened}
            onClick={() => setOpened((o) => !o)}
            hiddenFrom="sm"
            size="sm"
          />
          <Image
            src={eliseAILogo}
            alt="EliseAI"
            height={35}
            fit="contain"
          />
          {organization?.logoUrl && (
            <Image
              src={organization.logoUrl}
              alt={organization.name}
              height={35}
              fit="contain"
              ml="md"
            />
          )}
        </Group>
        
        <Group align="center">
          <Button variant="light" color="green" onClick={openInvite} size="sm">
            {isInternal ? 'Invite External Users' : 'Invite Users'}
          </Button>
          <Button variant="light" onClick={openCreate} size="sm">
            New Community Group
          </Button>
          <SessionSelector />
          <UserMenu />
        </Group>
      </Group>

      {/* Invite Users Modal */}
      <Modal 
        opened={inviteOpen} 
        onClose={closeInvite} 
        title={<Title order={4}>{isInternal ? 'Invite External Users' : 'Invite Users'}</Title>}
        centered
        styles={{
          title: { fontWeight: 600, fontSize: '1.125rem' },
        }}
      >
        <Stack>
          <Text>
            {isInternal
              ? "Enter an external user's (customer or prospect) email address to help fill out the information in this portal. They will receive an invite by email."
              : "Enter a colleague's email address to help fill out the information in this portal. They will receive an invite by email."}
          </Text>
          <TextInput
            label="Email"
            placeholder="name@company.com"
            {...inviteForm.getInputProps('email')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeInvite}>Cancel</Button>
            <Button color="green" onClick={handleInvite}>Invite User</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create Launch Group Modal */}
      <Modal 
        opened={createOpen} 
        onClose={noSessions ? () => {} : closeCreate}
        withCloseButton={!noSessions}
        closeOnClickOutside={!noSessions}
        closeOnEscape={!noSessions}
        title={<Title order={4}>Create Community Group</Title>} 
        centered
        styles={{
          title: { fontWeight: 600, fontSize: '1.125rem' },
        }}
      >
        <Stack>
          <Text>Create a new community group session to sign up a new set of communities.</Text>
          <TextInput
            label="Title"
            placeholder="e.g. Q4 Rollout"
            {...createForm.getInputProps('title')}
          />
          {isInternal && (
            <TextInput
              label="[Internal] Elise Org ID"
              placeholder="e.g. 123"
              inputMode="numeric"
              {...createForm.getInputProps('orgId')}
            />
          )}
          <Group justify="flex-end">
            {!noSessions && (
              <Button variant="default" onClick={closeCreate}>Cancel</Button>
            )}
            <Button onClick={handleCreate}>Create Community Group</Button>
          </Group>
        </Stack>
      </Modal>
    </AppShell.Header>
  );
});
